import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

export interface TabBarItem {
  key: string
  /** Drawn, not hidden. N2: five icons with no words was the noisiest thing on
   *  the screen and the one Doug asked to have labelled. */
  label: string
  /** A Lucide glyph at 24px. */
  icon: ReactNode
  /** A count. Absent and 0 both draw nothing — a badge reading "0" is a badge
   *  saying there is nothing to see. */
  badge?: number
  /** What the badge's number is a count of, as a phrase that includes it —
   *  "1 needs you". Read as the item's accessible name, "Lists, 1 needs you",
   *  where the badge's bare digit used to run into the label ("1Lists",
   *  a2-20). */
  badgeLabel?: string
}

export interface TabBarProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  items: TabBarItem[]
  active: string
  onChange: (key: string) => void
}

/** The bottom bar: a 24px icon with its label under it, and the safe-area inset
 *  below that. `--u-tabbar-h` is the one definition of its height, and the
 *  toast reads the same token to sit above it. */
export const TabBar = forwardRef<HTMLElement, TabBarProps>(function TabBar(
  { items, active, onChange, className = '', ...rest },
  ref,
) {
  return (
    <nav ref={ref} className={`u-tabbar ${className}`.trim()} {...rest}>
      {items.map(it => (
        <button
          key={it.key}
          type="button"
          className="u-tabbar__item"
          aria-current={it.key === active ? 'page' : undefined}
          aria-label={it.badge && it.badgeLabel ? `${it.label}, ${it.badgeLabel}` : undefined}
          onClick={() => onChange(it.key)}
        >
          <span className="u-tabbar__icon">
            {it.icon}
            {!!it.badge && <span className="u-tabbar__badge" aria-hidden="true">{it.badge}</span>}
          </span>
          <span className="u-tabbar__label">{it.label}</span>
        </button>
      ))}
    </nav>
  )
})
